class DiyAnimalCollection {
  constructor(root) {
    this.root = root;
    this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    this.moneyWithCurrencyFormat = root.dataset.moneyWithCurrencyFormat || this.moneyFormat;
    this.totalValueEl = root.querySelector('[data-diy-total-value]');
    this.messageEl = root.querySelector('[data-diy-message]');
    this.addButton = root.querySelector('[data-diy-add]');
    this.groupsEl = root.querySelector('.diy-everything__groups');
    this.emptyEl = root.querySelector('.diy-everything__empty');
    this.tagScanBaseUrl = root.dataset.tagScanBaseUrl || '';
    this.tagScanSectionId = 'view:accessory-tag-cards';
    this.tagScanView = 'accessory-tag-cards';
    this.tagCodes = this.readTagCodes(root.dataset.accessoryTagCodes || '');
    this.accessories = [];
    this.accessoryProductIds = new Set();
    this.petTag = root.querySelector('[data-pet-tag]');

    this.registerExistingAccessories();
    this.reorderTagGroups();
    this.initPetTag();

    if (this.addButton) {
      this.addButton.addEventListener('click', () => this.addAccessoriesToCart());
    }

    this.updateEmptyState();
    this.refreshUi();

    if (this.tagCodes.length) {
      this.scanTagMappings();
    }
  }

  readTagCodes(serialized) {
    return (serialized || '')
      .split('|')
      .map((code) => code.trim().toUpperCase())
      .filter(Boolean);
  }

  buildTagScanUrl(tagCode, page) {
    const baseUrl = new URL(this.tagScanBaseUrl || '/collections/all', window.location.origin);
    const normalizedBasePath = baseUrl.pathname.replace(/\/+$/, '');
    const url = new URL(normalizedBasePath || '/collections/all', window.location.origin);
    url.searchParams.set('view', this.tagScanView);
    url.searchParams.set('diy_tag', tagCode);
    url.searchParams.set('page', String(page));
    return url;
  }

  toTagHandle(tagCode) {
    return (tagCode || '')
      .trim()
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  registerExistingAccessories() {
    this.root.querySelectorAll('[data-diy-accessory]').forEach((accessory) => {
      this.registerAccessory(accessory);
    });
  }

  registerAccessory(accessory) {
    if (!accessory) return false;

    const productId = (accessory.dataset.productId || '').trim();
    if (productId && this.accessoryProductIds.has(productId)) {
      return false;
    }

    if (productId) {
      this.accessoryProductIds.add(productId);
    }

    this.accessories.push(accessory);
    this.initAccessory(accessory);
    return true;
  }

  initAccessory(accessory) {
    const select = accessory.querySelector('[data-diy-variant-select]');
    if (select) {
      accessory._variantOptions = this.readVariantOptions(select);
      if (!accessory._variantOptions.length) {
        accessory.hidden = true;
        this.updateAccessoryAvailability(accessory, false);
        return;
      }
      this.bindColorSwatches(accessory);
      this.bindOptionSelects(accessory);
      this.syncSelectedColorFromVariant(accessory);
      this.syncColorSelection(accessory);
      this.syncOptionSelectsFromVariant(accessory);

      select.addEventListener('change', () => {
        this.applySelectedVariant(accessory);
        this.syncSelectedColorFromVariant(accessory);
        this.syncOptionSelectsFromVariant(accessory);
        this.updateVariantFieldVisibility(accessory);
        this.refreshUi();
      });
    } else {
      this.updateVariantFieldVisibility(accessory);
    }

    this.applySelectedVariant(accessory);

    const qtyInput = accessory.querySelector('[data-diy-accessory-qty]');
    if (qtyInput) {
      qtyInput.addEventListener('change', () => this.refreshUi());
      qtyInput.addEventListener('input', () => this.refreshUi());
    }

    const cardAddButton = accessory.querySelector('[data-diy-accessory-add]');
    if (cardAddButton) {
      cardAddButton.addEventListener('click', () => this.addSingleAccessoryToCart(accessory, cardAddButton));
    }

    this.bindAccessoryPreview(accessory);
  }

  readQty(input) {
    const value = parseInt(input?.value || '0', 10);
    return Number.isFinite(value) ? Math.max(0, value) : 0;
  }

  isAvailable(accessory) {
    return accessory.dataset.available !== 'false';
  }

  readVariantOptions(select) {
    return Array.from(select.options)
      .map((option) => ({
        value: option.value || '',
        price: option.dataset.price || '0',
        available: option.dataset.available === 'true',
        inventoryQty: option.dataset.inventoryQty || '',
        inventoryManaged: option.dataset.inventoryManaged || '',
        url: option.dataset.url || '',
        image: option.dataset.image || '',
        previewImage: option.dataset.previewImage || option.dataset.image || '',
        color: option.dataset.color || '',
        primaryValue: option.dataset.primaryValue || '',
        secondaryValue: option.dataset.secondaryValue || '',
        primaryLabel: this.normalizeVariantLabel(option.dataset.primaryLabel || option.textContent.trim()),
        secondaryLabel: this.normalizeVariantLabel(option.dataset.secondaryLabel || ''),
        auxiliaryLabels: option.dataset.auxiliaryLabels || '',
        unitValue: option.dataset.unitValue || '1',
        unitLabel: option.dataset.unitLabel || '',
        variantTitle: option.dataset.variantTitle || option.textContent.trim(),
      }))
      .filter((option) => this.isVariantOptionSellable(option));
  }

  isVariantOptionSellable(option) {
    if (!option?.available) return false;

    const isInventoryManaged = option.inventoryManaged === 'true';
    if (!isInventoryManaged) return true;

    const inventoryQty = parseInt(option.inventoryQty || '0', 10);
    return Number.isFinite(inventoryQty) && inventoryQty > 0;
  }

  normalizeVariantLabel(label) {
    const value = String(label || '').trim();
    const normalized = value.toLowerCase();
    const normalizedWithoutPrefix = normalized.replace(/^(size|color|style)\s*:\s*/, '');
    return ['default title', 'default size', 'default color', 'default style', 'default value'].includes(normalizedWithoutPrefix)
      ? ''
      : value;
  }

  formatPrimaryLabel(accessory, label) {
    return this.formatVariantOptionLabel(accessory, 'primary', label);
  }

  formatVariantOptionLabel(accessory, role, label) {
    const value = this.normalizeVariantLabel(label);
    const optionName = role === 'secondary'
      ? (accessory?.dataset?.secondaryOptionName || '').trim().toLowerCase()
      : (accessory?.dataset?.primaryOptionName || '').trim().toLowerCase();
    if (optionName === 'size' && value && !/^size\s*:/i.test(value)) {
      return `Size: ${value}`;
    }
    return value;
  }

  bindOptionSelects(accessory) {
    accessory.querySelectorAll('[data-diy-option-select]').forEach((optionSelect) => {
      optionSelect.addEventListener('change', () => {
        accessory.dataset.lastOptionRole = optionSelect.dataset.optionRole || '';
        this.syncColorSelection(accessory);
        this.applySelectedVariant(accessory);
        this.syncSelectedColorFromVariant(accessory);
        this.syncOptionSelectsFromVariant(accessory);
        this.updateVariantFieldVisibility(accessory);
        this.refreshUi();
      });
    });
  }

  readSelectedOptionFilters(accessory) {
    const filters = {};
    accessory.querySelectorAll('[data-diy-option-select]').forEach((optionSelect) => {
      const role = (optionSelect.dataset.optionRole || '').trim();
      const value = optionSelect.value || '';
      if (role && value) {
        filters[role] = value;
      }
    });
    return filters;
  }

  getAvailableVariantOptions(accessory) {
    return (accessory._variantOptions || []).filter((option) => option.available);
  }

  getVariantOptionValue(option, role) {
    return role === 'secondary' ? option.secondaryValue : option.primaryValue;
  }

  getVariantOptionLabel(accessory, option, role) {
    const rawLabel = role === 'secondary'
      ? option.secondaryLabel || option.secondaryValue
      : option.primaryLabel || option.primaryValue || option.variantTitle;
    return this.formatVariantOptionLabel(accessory, role, rawLabel) || this.getVariantOptionValue(option, role);
  }

  uniqueVariantOptionChoices(accessory, options, role) {
    const seen = new Set();
    const choices = [];

    options.forEach((option) => {
      const value = this.getVariantOptionValue(option, role);
      if (!value || seen.has(value)) return;

      seen.add(value);
      choices.push({
        value,
        label: this.getVariantOptionLabel(accessory, option, role),
      });
    });

    return choices;
  }

  replaceOptionSelectChoices(optionSelect, choices, selectedValue) {
    const nextValue = choices.some((choice) => choice.value === selectedValue)
      ? selectedValue
      : choices[0]?.value || '';

    optionSelect.innerHTML = '';
    choices.forEach((choice) => {
      const option = document.createElement('option');
      option.value = choice.value;
      option.textContent = choice.label || choice.value;
      option.selected = choice.value === nextValue;
      optionSelect.appendChild(option);
    });

    if (nextValue) {
      optionSelect.value = nextValue;
    }

    return nextValue;
  }

  syncOptionValueChoices(accessory, scopedOptions) {
    const optionSelects = Array.from(accessory.querySelectorAll('[data-diy-option-select]'));
    if (!optionSelects.length) {
      return this.readSelectedOptionFilters(accessory);
    }

    const lastChangedRole = accessory.dataset.lastOptionRole || '';
    const orderedSelects = optionSelects.slice().sort((a, b) => {
      const aRole = a.dataset.optionRole || '';
      const bRole = b.dataset.optionRole || '';
      if (aRole === lastChangedRole) return -1;
      if (bRole === lastChangedRole) return 1;
      return 0;
    });
    const selected = this.readSelectedOptionFilters(accessory);

    orderedSelects.forEach((optionSelect) => {
      const role = (optionSelect.dataset.optionRole || '').trim();
      if (!role) return;

      const otherRole = role === 'secondary' ? 'primary' : 'secondary';
      const otherValue = role === lastChangedRole ? '' : selected[otherRole] || '';
      const candidates = otherValue
        ? scopedOptions.filter((option) => this.getVariantOptionValue(option, otherRole) === otherValue)
        : scopedOptions;
      const choices = this.uniqueVariantOptionChoices(
        accessory,
        candidates.length ? candidates : scopedOptions,
        role
      );
      selected[role] = this.replaceOptionSelectChoices(optionSelect, choices, selected[role] || optionSelect.value || '');
    });

    delete accessory.dataset.lastOptionRole;
    return selected;
  }

  syncOptionSelectsFromVariant(accessory) {
    const select = accessory.querySelector('[data-diy-variant-select]');
    if (!select) return;

    const currentOption =
      (accessory._variantOptions || []).find((option) => option.value === select.value) ||
      (accessory._variantOptions || []).find((option) => option.value === accessory.dataset.variantId) ||
      (accessory._variantOptions || [])[0];

    if (!currentOption) return;

    accessory.querySelectorAll('[data-diy-option-select]').forEach((optionSelect) => {
      const role = (optionSelect.dataset.optionRole || '').trim();
      const nextValue = role === 'secondary' ? currentOption.secondaryValue : currentOption.primaryValue;
      if (nextValue && optionSelect.value !== nextValue) {
        optionSelect.value = nextValue;
      }
    });
  }

  bindColorSwatches(accessory) {
    const swatches = accessory.querySelectorAll('[data-diy-color-swatch]');
    if (!swatches.length) return;

    swatches.forEach((swatch) => {
      swatch.addEventListener('click', () => {
        const nextColor = swatch.dataset.color || '';
        if (!nextColor) return;
        accessory.dataset.selectedColor = nextColor;
        this.syncColorSelection(accessory);
        this.applySelectedVariant(accessory);
        this.refreshUi();
      });
    });
  }

  syncSelectedColorFromVariant(accessory) {
    const select = accessory.querySelector('[data-diy-variant-select]');
    if (!select) return;

    const currentOption =
      (accessory._variantOptions || []).find((option) => option.value === select.value) ||
      (accessory._variantOptions || [])[0];

    if (currentOption?.color) {
      accessory.dataset.selectedColor = currentOption.color;
    }

    this.updateSwatchState(accessory);
  }

  syncColorSelection(accessory) {
    const select = accessory.querySelector('[data-diy-variant-select]');
    const allOptions = this.getAvailableVariantOptions(accessory);
    if (!select || !allOptions.length) return;

    let selectedColor = accessory.dataset.selectedColor || '';
    const hasColorSwatches = accessory.querySelectorAll('[data-diy-color-swatch]').length > 0;
    if (selectedColor && !allOptions.some((option) => option.color === selectedColor)) {
      selectedColor = '';
      delete accessory.dataset.selectedColor;
    }
    if (!selectedColor && hasColorSwatches) {
      selectedColor = allOptions.find((option) => option.color)?.color || '';
      if (selectedColor) accessory.dataset.selectedColor = selectedColor;
    }
    const filteredOptions = selectedColor
      ? allOptions.filter((option) => option.color === selectedColor)
      : allOptions;
    const colorOptions = filteredOptions.length ? filteredOptions : allOptions;
    const optionFilters = this.syncOptionValueChoices(accessory, colorOptions);
    let nextOptions = colorOptions.filter((option) => {
      if (optionFilters.primary && option.primaryValue !== optionFilters.primary) return false;
      if (optionFilters.secondary && option.secondaryValue !== optionFilters.secondary) return false;
      return true;
    });

    if (!nextOptions.length && optionFilters.primary) {
      nextOptions = colorOptions.filter((option) => option.primaryValue === optionFilters.primary);
    }

    if (!nextOptions.length) {
      nextOptions = colorOptions;
    }

    const currentVariantId = accessory.dataset.variantId || select.value;

    const nextSelected =
      nextOptions.find((option) => option.value === currentVariantId) ||
      nextOptions.find((option) => option.available) ||
      nextOptions[0];

    select.innerHTML = '';
    nextOptions.forEach((optionData) => {
      const option = document.createElement('option');
      const optionLabel = this.formatPrimaryLabel(accessory, optionData.primaryLabel || optionData.variantTitle || '');
      option.value = optionData.value;
      option.textContent = optionLabel;
      option.dataset.price = optionData.price;
      option.dataset.available = optionData.available ? 'true' : 'false';
      option.dataset.url = optionData.url;
      option.dataset.image = optionData.image;
      option.dataset.previewImage = optionData.previewImage;
      option.dataset.color = optionData.color;
      option.dataset.primaryValue = optionData.primaryValue;
      option.dataset.secondaryValue = optionData.secondaryValue;
      option.dataset.primaryLabel = optionLabel;
      option.dataset.secondaryLabel = optionData.secondaryLabel;
      option.dataset.auxiliaryLabels = optionData.auxiliaryLabels;
      option.dataset.unitValue = optionData.unitValue;
      option.dataset.unitLabel = optionData.unitLabel;
      option.dataset.variantTitle = optionData.variantTitle;
      option.selected = optionData.value === nextSelected?.value;
      select.appendChild(option);
    });

    if (nextSelected?.value) {
      select.value = nextSelected.value;
    }

    this.updateSwatchState(accessory);
    this.syncOptionSelectsFromVariant(accessory);
    this.updateVariantFieldVisibility(accessory, nextOptions, nextSelected);
  }

  updateSwatchState(accessory) {
    const selectedColor = accessory.dataset.selectedColor || '';
    const availableColors = new Set(
      this.getAvailableVariantOptions(accessory)
        .map((option) => option.color)
        .filter(Boolean)
    );
    accessory.querySelectorAll('[data-diy-color-swatch]').forEach((swatch) => {
      const swatchColor = swatch.dataset.color || '';
      const isUnavailable = availableColors.size > 0 && !availableColors.has(swatchColor);
      swatch.hidden = isUnavailable;
      const isActive = !isUnavailable && swatchColor === selectedColor;
      swatch.classList.toggle('is-active', isActive);
      swatch.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  updateVariantFieldVisibility(accessory, options = null, currentOption = null) {
    const select = accessory.querySelector('[data-diy-variant-select]');
    const staticField = accessory.querySelector('[data-diy-variant-static]');
    const wrap = accessory.querySelector('[data-diy-variant-wrap]');
    const swatchContainer = accessory.querySelector('[data-diy-color-swatches]');
    const optionSelects = Array.from(accessory.querySelectorAll('[data-diy-option-select]'));
    const primaryOptionName = (accessory.dataset.primaryOptionName || '').trim().toLowerCase();
    if (!wrap) return;

    if (optionSelects.length) {
      const hasVisibleOptionSelect = optionSelects.some((optionSelect) =>
        Array.from(optionSelect.options).some((option) => option.textContent.trim() !== '')
      );
      wrap.hidden = !hasVisibleOptionSelect;
      optionSelects.forEach((optionSelect) => {
        optionSelect.hidden = !hasVisibleOptionSelect;
      });
      if (select) select.hidden = true;
      if (staticField) {
        staticField.hidden = true;
        staticField.textContent = '';
      }
      return;
    }

    if (swatchContainer && swatchContainer.children.length > 0 && primaryOptionName === 'color') {
      wrap.hidden = true;
      if (select) select.hidden = true;
      if (staticField) {
        staticField.hidden = true;
        staticField.textContent = '';
      }
      return;
    }

    const nextOptions = options || accessory._variantOptions || [];
    const activeOption =
      currentOption ||
      nextOptions.find((option) => option.value === select?.value) ||
      nextOptions[0] ||
      null;
    const currentLabel = this.formatPrimaryLabel(accessory, activeOption?.primaryLabel || '');

    if (!select && staticField) {
      const staticContent = this.formatPrimaryLabel(accessory, staticField.textContent);
      const hasStaticContent = staticContent !== '';
      wrap.hidden = !hasStaticContent;
      staticField.hidden = !hasStaticContent;
      staticField.textContent = hasStaticContent ? staticContent : '';
      return;
    }

    const hasMeaningfulContent = currentLabel !== '';
    if (!hasMeaningfulContent) {
      wrap.hidden = true;
      if (select) select.hidden = true;
      if (staticField) {
        staticField.hidden = true;
        staticField.textContent = '';
      }
      return;
    }

    wrap.hidden = false;
    if ((nextOptions || []).length > 1 && select) {
      select.hidden = false;
      if (staticField) {
        staticField.hidden = true;
        staticField.textContent = '';
      }
      return;
    }

    if (select) select.hidden = true;
    if (staticField) {
      staticField.textContent = currentLabel;
      staticField.hidden = false;
    }
  }

  applySelectedVariant(accessory) {
    const select = accessory.querySelector('[data-diy-variant-select]');
    if (!select) {
      this.updateAccessoryUnitPrice(accessory);
      this.updateAccessoryAvailability(accessory, this.isAvailable(accessory));
      this.updateAccessoryAuxiliary(accessory, null);
      return;
    }

    const option = select.options[select.selectedIndex];
    if (!option) return;

    accessory.dataset.variantId = option.value || '';
    accessory.dataset.price = option.dataset.price || '0';
    accessory.dataset.available = option.dataset.available === 'true' ? 'true' : 'false';
    accessory.dataset.unitValue = option.dataset.unitValue || '1';
    accessory.dataset.unitLabel = option.dataset.unitLabel || '';
    accessory.dataset.previewImage = option.dataset.previewImage || option.dataset.image || accessory.dataset.defaultPreviewImage || '';

    this.updateAccessoryMedia(accessory, option.dataset.image || accessory.dataset.defaultImage || '');

    this.updateAccessoryUnitPrice(accessory);
    this.updateAccessoryAvailability(accessory, option.dataset.available === 'true');
    this.updateAccessoryAuxiliary(accessory, option);
  }

  updateAccessoryAuxiliary(accessory, option) {
    const metaWrap = accessory.querySelector('[data-diy-meta-wrap]');
    const metaList = accessory.querySelector('[data-diy-variant-meta-list]');
    if (!metaWrap || !metaList) return;

    const auxiliaryLabelsRaw = option ? (option.dataset.auxiliaryLabels || '') : '';
    const auxiliaryLabels = auxiliaryLabelsRaw
      .split('||')
      .map((label) => this.normalizeAuxiliaryLabel(label))
      .filter(Boolean);

    if (!option) {
      const existingItems = Array.from(metaList.querySelectorAll('[data-diy-variant-meta]'))
        .map((node) => node.textContent.trim())
        .filter(Boolean);
      if (existingItems.length) {
        metaWrap.hidden = false;
        metaList.hidden = false;
        return;
      }
    }

    metaList.innerHTML = auxiliaryLabels
      .map((label) => `<div class="diy-everything-accessory__meta" data-diy-variant-meta>${this.escapeHtml(label)}</div>`)
      .join('');

    const hasAuxiliaryLabels = auxiliaryLabels.length > 0;
    metaWrap.hidden = !hasAuxiliaryLabels;
    metaList.hidden = !hasAuxiliaryLabels;
  }

  normalizeAuxiliaryLabel(label) {
    const value = String(label || '').trim();
    const normalized = value.toLowerCase().replace(/\s+/g, ' ');
    const normalizedWithoutPrefix = normalized.replace(/^(size|color|style)\s*:\s*/, '');
    return ['default title', 'default size', 'default color', 'default style', 'default value'].includes(normalizedWithoutPrefix)
      ? ''
      : value;
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  updateAccessoryMedia(accessory, imageUrl) {
    if (!imageUrl) return;
    const mediaImage = accessory.querySelector('.diy-everything-accessory__media img');
    if (!mediaImage) return;
    mediaImage.removeAttribute('srcset');
    mediaImage.src = imageUrl;
    mediaImage.dataset.src = imageUrl;
  }

  bindAccessoryPreview(accessory) {
    const previewButton = accessory.querySelector('[data-diy-accessory-preview]');
    if (!previewButton) return;

    previewButton.addEventListener('click', () => this.openAccessoryPreview(accessory, previewButton));
  }

  getAccessoryPreviewModal() {
    if (this.previewModal) return this.previewModal;

    const modal = document.createElement('div');
    modal.className = 'diy-accessory-lightbox';
    modal.setAttribute('data-diy-accessory-lightbox', '');
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-hidden', 'true');
    modal.hidden = true;
    modal.innerHTML = `
      <button class="diy-accessory-lightbox__backdrop" type="button" data-diy-lightbox-close aria-label="Close image preview"></button>
      <div class="diy-accessory-lightbox__panel" role="document">
        <button class="diy-accessory-lightbox__close" type="button" data-diy-lightbox-close aria-label="Close image preview">&times;</button>
        <div class="diy-accessory-lightbox__media">
          <img data-diy-lightbox-image alt="">
        </div>
        <div class="diy-accessory-lightbox__info">
          <h3 class="diy-accessory-lightbox__title" data-diy-lightbox-title></h3>
          <p class="diy-accessory-lightbox__price" data-diy-lightbox-price></p>
          <div class="diy-accessory-lightbox__details" data-diy-lightbox-details></div>
        </div>
      </div>
    `;

    modal.querySelectorAll('[data-diy-lightbox-close]').forEach((closeButton) => {
      closeButton.addEventListener('click', () => this.closeAccessoryPreview());
    });

    document.body.appendChild(modal);
    this.previewModal = modal;
    this.previewKeydownHandler = (event) => {
      if (event.key === 'Escape' && this.previewModal && !this.previewModal.hidden) {
        this.closeAccessoryPreview();
      }
    };
    document.addEventListener('keydown', this.previewKeydownHandler);

    return modal;
  }

  readAccessoryPreviewDetails(accessory) {
    const details = [];
    const swatchContainer = accessory.querySelector('[data-diy-color-swatches]');
    const selectedColor = (accessory.dataset.selectedColor || '').trim();
    if (swatchContainer && selectedColor) {
      details.push(`Color: ${selectedColor}`);
    }

    const select = accessory.querySelector('[data-diy-variant-select]');
    const optionSelects = Array.from(accessory.querySelectorAll('[data-diy-option-select]'));
    if (optionSelects.length) {
      optionSelects.forEach((optionSelect) => {
        if (!optionSelect.hidden && optionSelect.selectedIndex >= 0) {
          const selectedText = (optionSelect.options[optionSelect.selectedIndex]?.textContent || '').trim();
          if (selectedText) details.push(selectedText);
        }
      });
    } else if (select && !select.hidden && select.selectedIndex >= 0) {
      const selectedText = (select.options[select.selectedIndex]?.textContent || '').trim();
      if (selectedText) details.push(selectedText);
    }

    const staticField = accessory.querySelector('[data-diy-variant-static]');
    const staticText = staticField && !staticField.hidden ? staticField.textContent.trim() : '';
    if (staticText) details.push(staticText);

    accessory.querySelectorAll('[data-diy-variant-meta]').forEach((meta) => {
      const text = meta.textContent.trim();
      if (text) details.push(text);
    });

    return [...new Set(details)];
  }

  openAccessoryPreview(accessory, trigger = null) {
    const imageUrl = accessory.dataset.previewImage || accessory.dataset.defaultPreviewImage || accessory.dataset.defaultImage || '';
    if (!imageUrl) return;

    const modal = this.getAccessoryPreviewModal();
    const image = modal.querySelector('[data-diy-lightbox-image]');
    const title = modal.querySelector('[data-diy-lightbox-title]');
    const price = modal.querySelector('[data-diy-lightbox-price]');
    const details = modal.querySelector('[data-diy-lightbox-details]');
    const accessoryTitle = accessory.dataset.productTitle || 'Accessory';
    const accessoryPrice = accessory.querySelector('[data-diy-unit-price]')?.textContent?.trim() || '';
    const accessoryDetails = this.readAccessoryPreviewDetails(accessory);

    this.previewLastFocused = trigger || document.activeElement;
    if (image) {
      image.src = imageUrl;
      image.alt = accessoryTitle;
    }
    if (title) title.textContent = accessoryTitle;
    if (price) price.textContent = accessoryPrice;
    if (details) {
      details.innerHTML = accessoryDetails
        .map((detail) => `<span>${this.escapeHtml(detail)}</span>`)
        .join('');
      details.hidden = accessoryDetails.length === 0;
    }

    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('diy-lightbox-open');
    modal.querySelector('[data-diy-lightbox-close]')?.focus({ preventScroll: true });
  }

  closeAccessoryPreview() {
    const modal = this.previewModal;
    if (!modal) return;

    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('diy-lightbox-open');

    const image = modal.querySelector('[data-diy-lightbox-image]');
    if (image) image.removeAttribute('src');

    if (this.previewLastFocused && typeof this.previewLastFocused.focus === 'function') {
      this.previewLastFocused.focus({ preventScroll: true });
    }
    this.previewLastFocused = null;
  }

  updateAccessoryUnitPrice(accessory) {
    const priceEl = accessory.querySelector('[data-diy-unit-price]');
    if (!priceEl) return;
    const cents = parseInt(accessory.dataset.price || '0', 10);
    const unitValue = (accessory.dataset.unitValue || '1').trim() || '1';
    const customUnitLabel = (accessory.dataset.unitLabel || '').trim();
    const unitNumber = Number.parseFloat(unitValue);
    const shouldUsePluralPieces = Number.isFinite(unitNumber) && unitNumber > 1;
    const normalizedUnitLabel = customUnitLabel.toLowerCase();
    const isPieceUnit = ['piece', 'pieces', 'pcs'].includes(normalizedUnitLabel);
    const unitLabel = !customUnitLabel || isPieceUnit
      ? (shouldUsePluralPieces ? 'pieces' : 'piece')
      : customUnitLabel;
    priceEl.textContent = `${this.formatMoney(Number.isFinite(cents) ? cents : 0)} / ${unitValue} ${unitLabel}`;
  }

  updateAccessoryAvailability(accessory, available) {
    accessory.dataset.available = available ? 'true' : 'false';
    accessory.classList.toggle('diy-everything-accessory--unavailable', !available);

    const input = accessory.querySelector('[data-diy-accessory-qty]');
    if (!input) return;

    if (!available) {
      input.value = 0;
      input.disabled = true;
      return;
    }

    input.disabled = false;
  }

  updateEmptyState() {
    const hasAccessories = this.accessories.length > 0;
    if (this.emptyEl) {
      this.emptyEl.hidden = hasAccessories;
    }
    if (this.groupsEl) {
      this.groupsEl.hidden = !hasAccessories;
    }
  }

  findGroupSection(tagCode) {
    const normalizedKey = (tagCode || '').trim().toLowerCase();
    return Array.from(this.root.querySelectorAll('[data-diy-group]')).find(
      (group) => (group.dataset.diyGroup || '').trim().toLowerCase() === normalizedKey
    );
  }

  ensureTagGroup(tagCode) {
    let groupSection = this.findGroupSection(tagCode);
    if (groupSection) {
      groupSection.hidden = false;
      this.reorderTagGroups();
      return groupSection;
    }

    if (!this.groupsEl) return null;

    groupSection = document.createElement('section');
    groupSection.className = 'diy-everything__group';
    groupSection.dataset.diyGroup = tagCode.toLowerCase();

    const title = document.createElement('h3');
    title.className = 'diy-everything__group-title';
    title.textContent = tagCode;

    const track = document.createElement('div');
    track.className = 'diy-everything__group-track';

    groupSection.appendChild(title);
    groupSection.appendChild(track);
    this.groupsEl.appendChild(groupSection);
    this.reorderTagGroups();
    return groupSection;
  }

  createLoadingPlaceholder() {
    const placeholder = document.createElement('article');
    placeholder.className = 'diy-everything-accessory diy-everything-accessory--placeholder';
    placeholder.setAttribute('data-diy-loading-placeholder', '');
    placeholder.setAttribute('aria-hidden', 'true');
    placeholder.innerHTML = `
      <div class="diy-everything-accessory__media diy-everything-accessory__media-placeholder-block"></div>
      <div class="diy-everything-accessory__line diy-everything-accessory__line--price"></div>
      <div class="diy-everything-accessory__line diy-everything-accessory__line--variant"></div>
      <div class="diy-everything-accessory__line diy-everything-accessory__line--qty"></div>
      <div class="diy-everything-accessory__line diy-everything-accessory__line--button"></div>
    `;
    return placeholder;
  }

  setGroupLoading(groupSection, isLoading, placeholderCount = 4) {
    if (!groupSection) return;
    const track = groupSection.querySelector('.diy-everything__group-track');
    if (!track) return;

    groupSection.classList.toggle('is-loading', Boolean(isLoading));
    track.querySelectorAll('[data-diy-loading-placeholder]').forEach((node) => node.remove());

    if (!isLoading) return;

    const hasRealItems = Boolean(track.querySelector('[data-diy-accessory]'));
    if (hasRealItems) return;

    for (let index = 0; index < placeholderCount; index += 1) {
      track.appendChild(this.createLoadingPlaceholder());
    }
  }

  reorderTagGroups() {
    if (!this.groupsEl || !this.tagCodes.length) return;

    const groups = Array.from(this.groupsEl.querySelectorAll('[data-diy-group]'));
    if (!groups.length) return;

    const primaryGroups = [];
    const matchedGroups = [];
    const unmatchedGroups = [];

    groups.forEach((group, originalIndex) => {
      const groupSource = (group.dataset.diyGroupSource || '').trim();
      if (groupSource === 'primary-products') {
        primaryGroups.push({ group, originalIndex });
        return;
      }

      const groupKey = (group.dataset.diyGroup || '').trim().toLowerCase();
      const tagIndex = this.tagCodes.findIndex((tagCode) => tagCode.trim().toLowerCase() === groupKey);
      if (tagIndex >= 0) {
        matchedGroups.push({ group, tagIndex });
      } else {
        unmatchedGroups.push({ group, originalIndex });
      }
    });

    primaryGroups.sort((left, right) => left.originalIndex - right.originalIndex);
    matchedGroups.sort((left, right) => left.tagIndex - right.tagIndex);
    unmatchedGroups.sort((left, right) => left.originalIndex - right.originalIndex);

    [
      ...primaryGroups.map((entry) => entry.group),
      ...matchedGroups.map((entry) => entry.group),
      ...unmatchedGroups.map((entry) => entry.group),
    ].forEach((group) => {
      this.groupsEl.appendChild(group);
    });
  }

  async scanTagMappings() {
    for (const tagCode of this.tagCodes) {
      try {
        await this.scanSingleTag(tagCode);
      } catch (error) {
        console.error(`Unable to scan accessory tag ${tagCode}`, error);
      }
    }
  }

  async scanSingleTag(tagCode) {
    if (!this.tagScanBaseUrl || !tagCode) return;

    let totalPages = 1;
    let groupSection = this.findGroupSection(tagCode);
    let track = groupSection?.querySelector('.diy-everything__group-track') || null;
    const hasExistingItems = Boolean(track?.querySelector('[data-diy-accessory]'));
    const groupSource = (groupSection?.dataset.diyGroupSource || '').trim();
    if (hasExistingItems && groupSource === 'primary-tag') {
      return;
    }
    // Existing Liquid output is not equivalent to page 1 of the exact tag scan.
    // Always scan from page 1 and let product-id de-duping avoid duplicate cards.
    let page = 1;
    let appendedAny = false;

    if (!groupSection) {
      groupSection = this.ensureTagGroup(tagCode);
      if (!groupSection) return;
      track = groupSection.querySelector('.diy-everything__group-track');
    }

    if (!track) return;
    this.setGroupLoading(groupSection, !hasExistingItems);

    while (page <= totalPages) {
      const requestUrl = this.buildTagScanUrl(tagCode, page);
      const response = await fetch(requestUrl.toString(), {
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        break;
      }

      const html = await response.text();
      if (!html.trim()) {
        break;
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const results = doc.querySelector('.diy-accessory-tag-scan__results');

      if (!results) break;

      const nextTotalPages = parseInt(results.dataset.diyScanPages || '1', 10);
      totalPages = Number.isFinite(nextTotalPages) && nextTotalPages > 0 ? nextTotalPages : 1;

      results.querySelectorAll('[data-diy-accessory]').forEach((accessory) => {
        if (this.registerAccessory(accessory)) {
          track.appendChild(accessory);
          appendedAny = true;
        }
      });

      page += 1;
    }

    groupSection.hidden = track.children.length === 0;
    if (groupSection.hidden) {
      groupSection.remove();
    } else {
      this.setGroupLoading(groupSection, false);
    }
    this.reorderTagGroups();

    if (appendedAny || hasExistingItems) {
      this.updateEmptyState();
      this.refreshUi();
    }
  }

  initPetTag() {
    if (!this.petTag) return;

    this.petTagToggle = this.petTag.querySelector('[data-pet-tag-toggle]');
    this.petTagBody = this.petTag.querySelector('[data-pet-tag-body]');
    this.petTagVariantSelect = this.petTag.querySelector('[data-pet-tag-variant-select]');
    this.petTagTextInput = this.petTag.querySelector('[data-pet-tag-text]');
    this.petTagFontSelect = this.petTag.querySelector('[data-pet-tag-font]');
    this.petTagPreviewText = this.petTag.querySelector('[data-pet-tag-preview-text]');
    this.petTagPriceEl = this.petTag.querySelector('[data-pet-tag-price]');
    this.petTagCountEl = this.petTag.querySelector('[data-pet-tag-count]');

    this.petTagToggle?.addEventListener('change', () => {
      this.petTag.dataset.enabled = this.petTagToggle.checked ? 'true' : 'false';
      if (this.petTagBody) this.petTagBody.hidden = !this.petTagToggle.checked;
      this.refreshUi();
    });

    this.petTagVariantSelect?.addEventListener('change', () => {
      this.applySelectedPetTagVariant();
      this.refreshUi();
    });

    this.petTagTextInput?.addEventListener('input', () => {
      this.updatePetTagText();
      this.refreshUi();
    });

    this.petTagFontSelect?.addEventListener('change', () => {
      this.updatePetTagFont();
      this.refreshUi();
    });

    this.applySelectedPetTagVariant();
    this.updatePetTagText();
    this.updatePetTagFont();
  }

  isPetTagEnabled() {
    return this.petTag?.dataset.enabled === 'true';
  }

  applySelectedPetTagVariant() {
    if (!this.petTag || !this.petTagVariantSelect) return;

    const option = this.petTagVariantSelect.options[this.petTagVariantSelect.selectedIndex];
    if (!option) return;

    this.petTag.dataset.variantId = option.value || '';
    this.petTag.dataset.price = option.dataset.price || '0';
    this.petTag.dataset.available = option.dataset.available === 'true' ? 'true' : 'false';
    this.petTag.dataset.style = option.dataset.style || option.textContent.trim();

    if (this.petTagPriceEl) {
      const cents = parseInt(this.petTag.dataset.price || '0', 10);
      this.petTagPriceEl.textContent = this.formatMoney(Number.isFinite(cents) ? cents : 0);
    }

    if (option.dataset.image) {
      this.updatePetTagImage(option.dataset.image);
    }
  }

  updatePetTagImage(imageUrl) {
    const imageEl = this.petTag?.querySelector('.diy-animal-tag__image');
    const wrap = this.petTag?.querySelector('.diy-animal-tag__image-wrap');
    if (!imageEl || !wrap) return;

    if (imageEl.tagName === 'IMG') {
      imageEl.removeAttribute('srcset');
      imageEl.src = imageUrl;
      return;
    }

    const img = document.createElement('img');
    img.className = 'diy-animal-tag__image';
    img.alt = '';
    img.src = imageUrl;
    imageEl.replaceWith(img);
  }

  updatePetTagText() {
    if (!this.petTagPreviewText || !this.petTagTextInput) return;

    const text = this.petTagTextInput.value.trim();
    this.petTagPreviewText.textContent = text || 'PET NAME';

    if (this.petTagCountEl) {
      this.petTagCountEl.textContent = Array.from(this.petTagTextInput.value || '').length;
    }
  }

  updatePetTagFont() {
    if (!this.petTagPreviewText || !this.petTagFontSelect) return;

    const classMap = {
      'Classic Serif': 'diy-animal-tag__preview-text--classic',
      'Elegant Serif': 'diy-animal-tag__preview-text--elegant',
      'Roman Capitals': 'diy-animal-tag__preview-text--roman',
      Editorial: 'diy-animal-tag__preview-text--editorial',
      'Modern Sans': 'diy-animal-tag__preview-text--modern',
      'Soft Sans': 'diy-animal-tag__preview-text--soft',
      Engraved: 'diy-animal-tag__preview-text--engraved',
      Monogram: 'diy-animal-tag__preview-text--monogram',
      Script: 'diy-animal-tag__preview-text--script',
      Handwritten: 'diy-animal-tag__preview-text--handwritten',
      Playful: 'diy-animal-tag__preview-text--playful',
      'Minimal Mono': 'diy-animal-tag__preview-text--mono',
    };
    Object.values(classMap).forEach((className) => this.petTagPreviewText.classList.remove(className));
    this.petTagPreviewText.classList.add(classMap[this.petTagFontSelect.value] || classMap['Classic Serif']);
  }

  readPetTagItem() {
    if (!this.isPetTagEnabled() || !this.petTag) return null;

    const variantId = this.petTag.dataset.variantId || '';
    const price = parseInt(this.petTag.dataset.price || '0', 10);
    const text = (this.petTagTextInput?.value || '').trim();
    const font = this.petTagFontSelect?.value || 'Classic Serif';
    const style = this.petTag.dataset.style || this.petTagVariantSelect?.selectedOptions?.[0]?.textContent?.trim() || 'Pet Tag';
    const available = this.petTag.dataset.available !== 'false';

    return {
      id: variantId,
      quantity: 1,
      price: Number.isFinite(price) ? price : 0,
      available,
      text,
      font,
      style,
    };
  }

  refreshUi() {
    this.accessories.forEach((accessory) => {
      this.updateAccessoryUnitPrice(accessory);

      const cardAddButton = accessory.querySelector('[data-diy-accessory-add]');
      if (!cardAddButton) return;

      const input = accessory.querySelector('[data-diy-accessory-qty]');
      const quantity = this.readQty(input);
      const disabled = !this.isAvailable(accessory) || !accessory.dataset.variantId || quantity <= 0;
      cardAddButton.disabled = disabled;
      cardAddButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    });

    const totalQty = this.accessories.reduce((sum, accessory) => {
      if (!this.isAvailable(accessory)) return sum;
      const input = accessory.querySelector('[data-diy-accessory-qty]');
      return sum + this.readQty(input);
    }, 0);

    const totalCents = this.accessories.reduce((sum, accessory) => {
      if (!this.isAvailable(accessory)) return sum;
      const input = accessory.querySelector('[data-diy-accessory-qty]');
      const qty = this.readQty(input);
      const price = parseInt(accessory.dataset.price || '0', 10);
      return sum + qty * (Number.isFinite(price) ? price : 0);
    }, 0);
    const petTagItem = this.readPetTagItem();
    const petTagTotalCents = petTagItem && petTagItem.available ? petTagItem.price : 0;
    const hasValidPetTag = Boolean(petTagItem && petTagItem.id && petTagItem.available && petTagItem.text);
    const wantsInvalidPetTag = Boolean(petTagItem && (!petTagItem.id || !petTagItem.available || !petTagItem.text));

    if (this.totalValueEl) {
      this.totalValueEl.textContent = this.formatMoney(totalCents + petTagTotalCents, true);
    }

    if (this.addButton) {
      const disabled = (totalQty === 0 && !hasValidPetTag) || wantsInvalidPetTag;
      this.addButton.disabled = disabled;
      this.addButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }

    this.showMessage('');
  }

  showMessage(message) {
    if (!this.messageEl) return;
    this.messageEl.textContent = message || '';
  }

  readAccessoryCartItem(accessory) {
    if (!this.isAvailable(accessory)) return null;

    const variantId = accessory.dataset.variantId || '';
    const input = accessory.querySelector('[data-diy-accessory-qty]');
    const quantity = this.readQty(input);

    if (!variantId || quantity <= 0) return null;

    return { id: variantId, quantity, accessory };
  }

  readParentProperties() {
    const parentId = this.root.dataset.diyParentId || '';
    const parentTitle = this.root.dataset.diyParentTitle || '';
    const parentUrl = this.root.dataset.diyParentUrl || window.location.pathname;
    const parentImage = this.root.dataset.diyParentImage || '';
    const parentQty = this.root.dataset.diyParentQty || '1';

    return {
      _diy_parent_id: parentId,
      _diy_parent_title: parentTitle,
      _diy_parent_url: parentUrl,
      _diy_parent_image: parentImage,
      _diy_parent_qty: parentQty,
    };
  }

  appendCartItem(formData, entry, index) {
    formData.append(`items[${index}][id]`, entry.id);
    formData.append(`items[${index}][quantity]`, entry.quantity);

    const unitProperties = entry.accessory
      ? (() => {
          const unitPriceCents = parseInt(entry.accessory.dataset.price || '', 10);
          return {
            _diy_unit_value: (entry.accessory.dataset.unitValue || '1').trim() || '1',
            _diy_unit_label: (entry.accessory.dataset.unitLabel || '').trim(),
            _diy_unit_price: Number.isFinite(unitPriceCents) ? this.formatMoney(unitPriceCents) : '',
            _diy_unit_price_cents: Number.isFinite(unitPriceCents) ? String(unitPriceCents) : '',
          };
        })()
      : {};

    const properties = {
      ...this.readParentProperties(),
      ...unitProperties,
      ...(entry.properties || {}),
    };

    Object.entries(properties).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value) !== '') {
        formData.append(`items[${index}][properties][${key}]`, value);
      }
    });
  }

  getCartAddBatches(items) {
    const normalizedItems = (items || [])
      .map((entry) => ({
        ...entry,
        quantity: Math.max(0, parseInt(entry?.quantity || '0', 10) || 0),
      }))
      .filter((entry) => entry.id && entry.quantity > 0);

    return normalizedItems.length ? [normalizedItems] : [];
  }

  buildCartFormData(batch, cart, button, includeSections) {
    const formData = new FormData();
    batch.forEach((entry, index) => this.appendCartItem(formData, entry, index));

    if (includeSections && cart && cart.getSectionsToRender) {
      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
      if (cart.setActiveElement) cart.setActiveElement(button);
    }

    return formData;
  }

  setButtonLoading(button, isLoading) {
    if (!button) return;

    const spinner = button.querySelector('.loading__spinner');
    button.classList.toggle('loading', isLoading);
    button.disabled = isLoading;
    button.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  }

  getRequestQuantities(items) {
    const quantities = new Map();
    items.forEach((entry) => {
      const variantId = String(entry?.id || '').trim();
      if (!variantId) return;
      quantities.set(variantId, (quantities.get(variantId) || 0) + (entry.quantity || 0));
    });
    return quantities;
  }

  getResponseQuantities(parsed) {
    const quantities = new Map();
    const responseItems = Array.isArray(parsed?.items) ? parsed.items : parsed ? [parsed] : [];

    responseItems.forEach((item) => {
      const variantId = String(item.variant_id || item.id || '').trim();
      const quantity = parseInt(item.quantity || '0', 10);
      if (!variantId || !Number.isFinite(quantity) || quantity <= 0) return;
      quantities.set(variantId, (quantities.get(variantId) || 0) + quantity);
    });

    return quantities;
  }

  getItemTitle(entry) {
    return (
      entry?.accessory?.dataset?.productTitle ||
      entry?.accessory?.querySelector?.('[data-diy-accessory-title]')?.textContent?.trim() ||
      entry?.properties?._diy_pet_tag_style ||
      'this item'
    );
  }

  getPartialAddMessage(items, parsed) {
    const requestedQuantities = this.getRequestQuantities(items);
    const responseQuantities = this.getResponseQuantities(parsed);
    if (!requestedQuantities.size || !responseQuantities.size) return '';

    const checkedVariantIds = new Set();
    for (const entry of items) {
      const variantId = String(entry?.id || '').trim();
      if (!variantId || checkedVariantIds.has(variantId)) continue;
      checkedVariantIds.add(variantId);

      const requested = requestedQuantities.get(variantId) || 0;
      const accepted = responseQuantities.get(variantId) || 0;

      if (accepted > 0 && accepted < requested) {
        return `Only ${accepted} of ${this.getItemTitle(entry)} could be added due to availability.`;
      }
    }

    return '';
  }

  getCartDeltaMessage(items, beforeQuantities, afterQuantities) {
    const requestedQuantities = this.getRequestQuantities(items);
    if (!requestedQuantities.size || !afterQuantities?.size) return '';

    const checkedVariantIds = new Set();
    for (const entry of items) {
      const variantId = String(entry?.id || '').trim();
      if (!variantId || checkedVariantIds.has(variantId)) continue;
      checkedVariantIds.add(variantId);

      const requested = requestedQuantities.get(variantId) || 0;
      const before = beforeQuantities?.get(variantId) || 0;
      const after = afterQuantities.get(variantId) || 0;
      const added = Math.max(0, after - before);

      if (added > 0 && added < requested) {
        return `Only ${added} of ${this.getItemTitle(entry)} could be added due to availability.`;
      }
    }

    return '';
  }

  async fetchCartQuantities() {
    const response = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Unable to load cart quantities: ${response.status}`);
    }

    const cartData = await response.json();
    const quantities = new Map();
    (cartData.items || []).forEach((item) => {
      const variantId = String(item.variant_id || item.id || '').trim();
      if (!variantId) return;
      quantities.set(variantId, (quantities.get(variantId) || 0) + (item.quantity || 0));
    });
    return quantities;
  }

  resetAccessoryQuantities(accessories) {
    accessories.forEach((accessory) => {
      const input = accessory?.querySelector?.('[data-diy-accessory-qty]');
      if (input) input.value = '0';
    });
  }

  async submitCartItems(items, button, resetAccessories = []) {
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };
    config.method = 'POST';
    config.headers = config.headers || {};
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    this.setButtonLoading(button, true);

    try {
      let beforeCartQuantities = new Map();
      try {
        beforeCartQuantities = await this.fetchCartQuantities();
      } catch (cartError) {
          console.warn(cartError);
      }
      const batches = this.getCartAddBatches(items);

      let lastRenderableResponse = null;
      let partialAddMessage = '';
      let requestErrorMessage = '';

      for (let index = 0; index < batches.length; index += 1) {
        const batch = batches[index];
        const includeSections = index === batches.length - 1;
        const requestConfig = { ...config, body: this.buildCartFormData(batch, cart, button, includeSections) };
        const response = await fetch(`${routes.cart_add_url}`, requestConfig);
        const parsed = await response.json();

        if (parsed.status) {
          requestErrorMessage = parsed.description || parsed.message || 'Unable to add accessories.';
          break;
        }

        if (!parsed.key && Array.isArray(parsed.items) && parsed.items.length) {
          parsed.key = parsed.items[0]?.key || '';
        }

        lastRenderableResponse = parsed;
        partialAddMessage = partialAddMessage || this.getPartialAddMessage(batch, parsed);
      }

      let cartDeltaMessage = '';
      try {
        const afterCartQuantities = await this.fetchCartQuantities();
        cartDeltaMessage = this.getCartDeltaMessage(items, beforeCartQuantities, afterCartQuantities);
      } catch (cartError) {
        console.warn(cartError);
      }

      if (lastRenderableResponse && cart && cart.renderContents) {
        cart.renderContents(lastRenderableResponse);
      } else if (!cart) {
        window.location = (typeof routes !== 'undefined' && routes.cart_url) ? routes.cart_url : '/cart';
      }

      if (lastRenderableResponse || cartDeltaMessage) {
        this.resetAccessoryQuantities(resetAccessories);
        this.showMessage(cartDeltaMessage || partialAddMessage || requestErrorMessage);
        return true;
      }

      this.showMessage(requestErrorMessage || 'Unable to add accessories.');
      return false;
    } catch (error) {
      console.error(error);
      this.showMessage('Unable to add accessories.');
      return false;
    } finally {
      this.setButtonLoading(button, false);
      this.refreshUi();
    }
  }

  async addSingleAccessoryToCart(accessory, button) {
    const item = this.readAccessoryCartItem(accessory);
    if (!item) {
      this.showMessage('Please select a quantity.');
      return;
    }

    await this.submitCartItems([item], button, [accessory]);
  }

  async addAccessoriesToCart() {
    if (!this.addButton) return;

    const items = this.accessories
      .map((accessory) => this.readAccessoryCartItem(accessory))
      .filter(Boolean);
    const petTagItem = this.readPetTagItem();

    if (petTagItem && !petTagItem.text) {
      this.showMessage('Please enter pet tag text.');
      return;
    }

    if (petTagItem && !petTagItem.available) {
      this.showMessage('Selected pet tag is unavailable.');
      return;
    }

    if (petTagItem && petTagItem.id) {
      items.push({
        id: petTagItem.id,
        quantity: 1,
        properties: {
          'Pet tag text': petTagItem.text,
          'Pet tag font': petTagItem.font,
          'Pet tag style': petTagItem.style,
        },
      });
    }

    if (!items.length) {
      this.showMessage('Please select accessories or a pet tag.');
      return;
    }

    await this.submitCartItems(items, this.addButton, items.map((item) => item.accessory));
  }

  formatMoney(cents, withCurrency = false) {
    const amount = Number.isFinite(cents) ? cents : 0;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const format = withCurrency ? this.moneyWithCurrencyFormat : this.moneyFormat;
      return window.Shopify.formatMoney(amount, format);
    }

    const currency = document.documentElement.dataset.currency || 'USD';
    const locale = document.documentElement.lang || 'en';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount / 100);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-diy-animal]').forEach((root) => {
    new DiyAnimalCollection(root);
  });
});
